import { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Link } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import Money from "@/components/Money";
import CountryFlagChips from "@/components/browse/CountryFlagChips";
import { useCurrency } from "@/lib/currency";

// Leaflet's default marker icon paths break under bundlers. Inline SVG removes the
// asset-loading hassle entirely and lets us style hover/active states ourselves.
const carPinSvg = (label) => {
  // Auto-shrink the font when the formatted price gets long (e.g. JPY ¥16,500 vs €100)
  // so it always stays inside the white circle. Keep the SVG width fixed.
  const size = label.length <= 4 ? 10 : label.length <= 6 ? 8.5 : 7.2;
  return `
<svg xmlns="http://www.w3.org/2000/svg" width="46" height="56" viewBox="0 0 46 56">
  <defs>
    <filter id="sd" x="-20%" y="-20%" width="140%" height="140%">
      <feDropShadow dx="0" dy="2" stdDeviation="2" flood-opacity="0.25"/>
    </filter>
  </defs>
  <path filter="url(#sd)" d="M23 2C12.5 2 4 10.4 4 20.8c0 13.6 19 33.2 19 33.2s19-19.6 19-33.2C42 10.4 33.5 2 23 2z" fill="#2563eb"/>
  <circle cx="23" cy="20" r="13" fill="#fff"/>
  <text x="23" y="${20 + size / 3}" text-anchor="middle" font-family="-apple-system,sans-serif" font-size="${size}" font-weight="700" fill="#1e3a8a">${label}</text>
</svg>`;
};

const makeIcon = (label) =>
  L.divIcon({
    html: carPinSvg(label),
    className: "rentalcar-car-pin",
    iconSize: [46, 56],
    iconAnchor: [23, 54],
    popupAnchor: [0, -50],
  });

// Centre of Europe-ish — far enough zoomed out that EU + UK fit comfortably on first paint.
const EU_CENTRE = [48.8, 9.5];
const EU_ZOOM = 4;

/**
 * Auto-fits the Leaflet viewport to the currently-visible pins whenever the filtered set
 * changes. Lives as a child of <MapContainer> so it can read the map instance via the
 * `useMap` hook (Leaflet's only sane way to imperatively control the map from React).
 *
 * Rules:
 *   • 0 pins         → keep the previous viewport. Nothing to fit; flying to no-bounds is
 *                       a Leaflet runtime error.
 *   • 1 pin          → centre on it at a city-level zoom so the renter sees a recognisable
 *                       neighbourhood, not just an empty country.
 *   • 2+ pins        → flyToBounds with sane padding + a maxZoom guard so two pins in the
 *                       same city don't zoom in past street-level.
 *
 * We rebuild the bounds key from the pin ids so unrelated re-renders (e.g. popup open/
 * close) don't trigger a re-fit.
 */
function AutoFitBounds({ pins }) {
  const map = useMap();
  // Concatenate pin ids → only fires when the set actually changes.
  const fingerprint = pins.map((p) => p.id).join(",");
  useEffect(() => {
    if (!map) return;
    // Instrumentation hook for Playwright/E2E: lets tests read the map's current
    // center/zoom without depending on Leaflet's transform-string ergonomics.
    if (typeof window !== "undefined") window.__driveNestMap = map;

    if (pins.length === 0) return;
    if (pins.length === 1) {
      map.flyTo([pins[0].lat, pins[0].lng], 11, { duration: 0.75 });
      return;
    }
    const bounds = L.latLngBounds(pins.map((p) => [p.lat, p.lng]));
    map.flyToBounds(bounds, {
      padding: [48, 48],
      maxZoom: 10, // never zoom past city-level even if two pins are in one city
      duration: 0.75,
    });
    // After the animation settles, stamp the resulting zoom on the container so it's
    // assertable from headless tests. Leaflet finishes the fly in ~750ms.
    const t = setTimeout(() => {
      const el = map.getContainer();
      if (el) el.setAttribute("data-leaflet-zoom", String(map.getZoom()));
    }, 850);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fingerprint, map]);
  return null;
}

export default function BrowseMap({ filteredCarIds = null, highlightedCarId = null, fill = false }) {
  const [pins, setPins] = useState(null);
  const [error, setError] = useState(null);
  const [countryFilter, setCountryFilter] = useState(null);
  // Pin labels respect the visitor's chosen display currency. We only need the formatter,
  // and a "currency" key so React re-renders the map when the user flips currencies.
  const { format, currency } = useCurrency();

  useEffect(() => {
    let cancelled = false;
    api.get("/cars/map")
      .then((r) => { if (!cancelled) setPins(r.data || []); })
      .catch((e) => { if (!cancelled) setError(e?.message || "Map load failed"); });
    return () => { cancelled = true; };
  }, []);

  // Two filter layers stack: the existing `filteredCarIds` (driven by BrowseFilters) AND
  // the country chip filter we just added. We intersect them — empty union ⇒ no pins.
  const visible = pins
    ? (() => {
        let v = pins;
        if (filteredCarIds && filteredCarIds.size > 0) v = v.filter((p) => filteredCarIds.has(p.id));
        if (countryFilter) v = v.filter((p) => p.country_code === countryFilter);
        return v;
      })()
    : null;

  if (error) {
    return <div className="rounded-3xl border border-border p-12 text-center text-neutral-500" data-testid="browse-map-error">Could not load map.</div>;
  }
  if (!visible) {
    return <div className={`${fill ? "h-full" : "h-[600px]"} rounded-3xl border border-border flex items-center justify-center text-neutral-500`} data-testid="browse-map-loading">Loading map…</div>;
  }

  // When `fill` is true the map fills its parent (used by the new split-screen
  // Browse layout where the parent is a sticky height-bounded container). When
  // false we render the legacy standalone version with country chips + fixed
  // 640px height — preserved so the mobile/tablet toggle keeps working.
  if (fill) {
    return (
      <div className="h-full w-full relative" data-testid="browse-map">
        <MapContainer
          center={EU_CENTRE}
          zoom={EU_ZOOM}
          scrollWheelZoom
          style={{ height: "100%", width: "100%" }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <AutoFitBounds pins={visible} />
          {visible.map((p) => (
            <Marker
              key={`${p.id}-${currency}`}
              position={[p.lat, p.lng]}
              icon={makeIcon(format(p.price_per_day).replace(/\.00$/, ""))}
              opacity={highlightedCarId && highlightedCarId !== p.id ? 0.55 : 1}
              zIndexOffset={highlightedCarId === p.id ? 1000 : 0}
            >
              <Popup>
                <div className="space-y-1.5 min-w-[180px]" data-testid={`map-popup-${p.id}`}>
                  {p.image && (
                    <img
                      src={fileUrl(p.image)}
                      alt={`${p.make} ${p.model}`}
                      className="h-24 w-full object-cover rounded-lg"
                    />
                  )}
                  <div className="font-semibold text-neutral-900 text-sm leading-tight">{p.make} {p.model}</div>
                  <div className="text-xs text-neutral-500">{p.location} · {p.car_type}</div>
                  {p.vendor_office && (
                    <div className="text-[11px] text-neutral-500" data-testid={`map-popup-office-${p.id}`}>
                      {p.vendor_name ? `${p.vendor_name} office: ` : "Office: "}{p.vendor_office}
                    </div>
                  )}
                  <div className="flex items-center justify-between pt-1">
                    <div className="text-sm font-semibold"><Money amount={p.price_per_day} /><span className="text-xs text-neutral-500 font-normal">/day</span></div>
                    <Link
                      to={`/cars/${p.id}`}
                      className="text-xs font-medium text-blue-600 hover:text-blue-700"
                      data-testid={`map-popup-cta-${p.id}`}
                    >
                      View →
                    </Link>
                  </div>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
        <div className="absolute bottom-2 left-2 px-2.5 py-1 text-[11px] text-neutral-700 bg-white/90 backdrop-blur rounded-full border border-border shadow-sm pointer-events-none">
          {visible.length} car{visible.length === 1 ? "" : "s"} on map
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="browse-map">
      {pins && <CountryFlagChips pins={pins} selected={countryFilter} onChange={setCountryFilter} />}
      <div className="rounded-3xl overflow-hidden border border-border">
      <MapContainer
        center={EU_CENTRE}
        zoom={EU_ZOOM}
        scrollWheelZoom
        style={{ height: 640, width: "100%" }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <AutoFitBounds pins={visible} />
        {visible.map((p) => (
          <Marker key={`${p.id}-${currency}`} position={[p.lat, p.lng]} icon={makeIcon(format(p.price_per_day).replace(/\.00$/, ""))}>
            <Popup>
              <div className="space-y-1.5 min-w-[180px]" data-testid={`map-popup-${p.id}`}>
                {p.image && (
                  <img
                    src={fileUrl(p.image)}
                    alt={`${p.make} ${p.model}`}
                    className="h-24 w-full object-cover rounded-lg"
                  />
                )}
                <div className="font-semibold text-neutral-900 text-sm leading-tight">{p.make} {p.model}</div>
                <div className="text-xs text-neutral-500">{p.location} · {p.car_type}</div>
                {p.vendor_office && (
                  <div className="text-[11px] text-neutral-500">
                    {p.vendor_name ? `${p.vendor_name} office: ` : "Office: "}{p.vendor_office}
                  </div>
                )}
                <div className="flex items-center justify-between pt-1">
                  <div className="text-sm font-semibold"><Money amount={p.price_per_day} /><span className="text-xs text-neutral-500 font-normal">/day</span></div>
                  <Link
                    to={`/cars/${p.id}`}
                    className="text-xs font-medium text-blue-600 hover:text-blue-700"
                    data-testid={`map-popup-cta-${p.id}`}
                  >
                    View →
                  </Link>
                </div>
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
      <div className="px-4 py-2 text-xs text-neutral-500 bg-neutral-50 border-t border-border">
        Showing {visible.length} car{visible.length === 1 ? "" : "s"} on map
      </div>
      </div>
    </div>
  );
}
